import { FC } from 'react';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Link from '@mui/material/Link';
import RoomRatesTableRateCell from 'components/RoomRatesTableRateCell';
import CateringOptionName from 'components/CateringOptionName';
import NonBreakingDate from 'components/NonBreakingDate';

import { RoomRatesTableDateRow_groupedRate$key } from '__generated__/RoomRatesTableDateRow_groupedRate.graphql';
import { RoomRatesTableDateRow_package$key } from '__generated__/RoomRatesTableDateRow_package.graphql';
import { RoomRatesTableDateRow_data$key } from '__generated__/RoomRatesTableDateRow_data.graphql';

type Props = {
  data: RoomRatesTableDateRow_data$key;
  groupedRate: RoomRatesTableDateRow_groupedRate$key;
  package: RoomRatesTableDateRow_package$key;
};

const RoomRatesTableDateRow: FC<Props> = props => {
  const data = useFragment(
    graphql`
      fragment RoomRatesTableDateRow_data on Query {
        ...RoomRatesTableRateCell_data
      }
    `,
    props.data
  );

  const groupedRate = useFragment(
    graphql`
      fragment RoomRatesTableDateRow_groupedRate on GroupedRate {
        date {
          id
          start
          end

          ...RoomRatesTableRateCell_packageDate
        }

        ratesByCatering {
          cateringOption {
            id
            ...CateringOptionName_cateringOption
            ...RoomRatesTableRateCell_cateringOption
          }

          ratesByRoom {
            __id
            ...RoomRatesTableRateCell_rateByRoom
          }
        }
      }
    `,
    props.groupedRate
  );

  const pack = useFragment(
    graphql`
      fragment RoomRatesTableDateRow_package on Package {
        ...RoomRatesTableRateCell_package
      }
    `,
    props.package
  );

  const {
    date: { id, start, end },
    ratesByCatering,
  } = groupedRate;

  return (
    <>
      {ratesByCatering.map(({ cateringOption, ratesByRoom }, i) => (
        <TableRow key={`${id}:${cateringOption.id}`}>
          {i === 0 && (
            <TableCell rowSpan={ratesByCatering.length}>
              <NonBreakingDate>{start}</NonBreakingDate> -{' '}
              <NonBreakingDate>{end}</NonBreakingDate>
            </TableCell>
          )}

          <TableCell>
            <Link href={`/settings/catering/${cateringOption.id}`}>
              <CateringOptionName cateringOption={cateringOption} />
            </Link>
          </TableCell>

          {ratesByRoom.map(rate => (
            <RoomRatesTableRateCell
              key={rate.__id}
              data={data}
              rateByRoom={rate}
              package={pack}
              packageDate={groupedRate.date}
              cateringOption={cateringOption}
            />
          ))}
        </TableRow>
      ))}
    </>
  );
};

export default RoomRatesTableDateRow;
